import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

export function MfaSettings() {
  const [status, setStatus] = useState(null);
  const [stage, setStage] = useState("idle"); // idle | setup | enabling | recovery | disabling
  const [pwd, setPwd] = useState("");
  const [code, setCode] = useState("");
  const [qr, setQr] = useState(null);
  const [manualKey, setManualKey] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/mfa/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup", { password: pwd });
      setQr(data.qr_png_base64); setManualKey(data.manual_key); setStage("enabling"); setPwd("");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code: code.trim() });
      setRecoveryCodes(data.recovery_codes); setStage("recovery"); setCode(""); load();
      toast.success("Two-factor authentication enabled");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Invalid code"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: pwd, code: code.trim() });
      setStage("idle"); setPwd(""); setCode(""); load();
      toast.success("Two-factor authentication disabled");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const regen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/recovery/regenerate", { password: pwd });
      setRecoveryCodes(data.recovery_codes); setStage("recovery"); setPwd(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="mfa-settings">
      <div className="flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5 text-primary" /><h3 className="font-heading text-base font-semibold">Two-Factor Authentication (MFA)</h3></div>

      <div className="mb-3 text-sm">
        Status:{" "}
        <span className={status?.enabled ? "font-semibold text-emerald-600" : "text-muted-foreground"} data-testid="mfa-status">
          {status?.enabled ? `Enabled · ${status.recovery_remaining} recovery codes left` : "Not enabled"}
        </span>
      </div>

      {recoveryCodes && (
        <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="mfa-recovery-codes">
          <div className="mb-1 font-semibold text-amber-800">Save these recovery codes now — shown only once:</div>
          <div className="grid grid-cols-2 gap-1 font-mono text-xs">{recoveryCodes.map((c) => <div key={c}>{c}</div>)}</div>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => { navigator.clipboard?.writeText(recoveryCodes.join("\n")); toast.success("Copied"); }}><Copy className="mr-1 h-3 w-3" /> Copy</Button>
          <Button size="sm" className="mt-2 ml-2" onClick={() => { setRecoveryCodes(null); setStage("idle"); }} data-testid="mfa-recovery-done">Done</Button>
        </div>
      )}

      {!recoveryCodes && !status?.enabled && stage === "idle" && (
        <Button size="sm" onClick={() => setStage("setup")} data-testid="mfa-enable-start">Enable MFA</Button>
      )}

      {!recoveryCodes && stage === "setup" && (
        <div className="space-y-2">
          <Label className="text-xs">Confirm your password to begin</Label>
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="mfa-setup-pwd" />
          <div className="flex gap-2">
            <Button size="sm" onClick={startSetup} disabled={busy} data-testid="mfa-setup-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}</Button>
            <Button size="sm" variant="outline" onClick={() => { setStage("idle"); setPwd(""); }}>Cancel</Button>
          </div>
        </div>
      )}

      {!recoveryCodes && stage === "enabling" && (
        <div className="space-y-2">
          {qr && <img src={`data:image/png;base64,${qr}`} alt="Scan in authenticator app" className="h-40 w-40 rounded bg-white p-1" data-testid="mfa-qr" />}
          <div className="text-xs text-muted-foreground">Manual key: <span className="font-mono">{manualKey}</span></div>
          <Label className="text-xs">Enter the 6-digit code to confirm</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="mfa-enable-code" />
          <div className="flex gap-2">
            <Button size="sm" onClick={enable} disabled={busy} data-testid="mfa-enable-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & enable"}</Button>
            <Button size="sm" variant="outline" onClick={() => setStage("idle")}>Cancel</Button>
          </div>
        </div>
      )}

      {!recoveryCodes && status?.enabled && stage === "idle" && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setStage("recovery-regen")} data-testid="mfa-regen-start">Regenerate recovery codes</Button>
          <Button size="sm" variant="outline" onClick={() => setStage("disabling")} data-testid="mfa-disable-start">Disable MFA</Button>
        </div>
      )}

      {!recoveryCodes && stage === "recovery-regen" && (
        <div className="space-y-2">
          <Label className="text-xs">Confirm password to regenerate</Label>
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="mfa-regen-pwd" />
          <div className="flex gap-2">
            <Button size="sm" onClick={regen} disabled={busy} data-testid="mfa-regen-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Regenerate"}</Button>
            <Button size="sm" variant="outline" onClick={() => { setStage("idle"); setPwd(""); }}>Cancel</Button>
          </div>
        </div>
      )}

      {!recoveryCodes && stage === "disabling" && (
        <div className="space-y-2">
          <Label className="text-xs">Password + current MFA (or recovery) code</Label>
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Password" data-testid="mfa-disable-pwd" />
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="MFA / recovery code" data-testid="mfa-disable-code" />
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" onClick={disable} disabled={busy} data-testid="mfa-disable-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable MFA"}</Button>
            <Button size="sm" variant="outline" onClick={() => { setStage("idle"); setPwd(""); setCode(""); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
